import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { setBackofficeChallenges } from '../../../backoffice/store/challenges/challengesSlice';
import { getChallenges, GetChallengesParams, toggleChallengeStatus } from '../../services/challenges';
import { EPillar, IChallenges } from '../../types/challenges';

const PAGE_SIZE = 10;

interface UseChallengesParams {
  initialSearchValue?: string;
  initialPage?: number;
  initialSortBy?: string;
  initialSortDir?: 'asc' | 'desc';
  selectedCategory?: EPillar | null;
  filterStatus?: string;
  filterType?: string;
}

export function useControll({
  initialSearchValue = '',
  initialPage = 1,
  initialSortBy = 'name',
  initialSortDir = 'asc',
  selectedCategory = null,
  filterStatus = '',
  filterType = '',
}: UseChallengesParams = {}) {
  const dispatch = useDispatch();

  const defaultTotals: Record<EPillar, number> = {
    [EPillar.SECURITY]: 0,
    [EPillar.ESG]: 0,
    [EPillar.PREDICTIVE]: 0,
    [EPillar.SAFE]: 0,
  };

  const [challengeTotals, setChallengeTotals] = useState<Record<EPillar, number>>(defaultTotals);
  const [challenges, setChallenges] = useState<IChallenges[]>([]);
  const [searchValue, setSearchValue] = useState(initialSearchValue);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [sortBy, setSortBy] = useState<string>(initialSortBy);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(initialSortDir);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [variables, setVariables] = useState<any[]>([]);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    setChallenges([]);
    setPage(1);
  }, [selectedCategory, filterStatus, filterType, searchValue, sortBy, sortDir]);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError(null);

      const params: GetChallengesParams = {
        page: page - 1,
        size: PAGE_SIZE,
        pillar: selectedCategory ?? undefined,
        status: filterStatus || undefined,
        type: filterType || undefined,
        search: searchValue || undefined,
        sortBy,
        sortDir,
      };

      try {
        const { data, totals, pagination } = await getChallenges(params);

        if (searchValue) {
          setChallenges(data);
        } else {
          setChallenges(prev =>
            page === 1
              ? data
              : [...prev, ...data]
          );
        }

        setChallengeTotals(totals);
        setTotalPages(searchValue ? 1 : pagination.totalPages);
        setTotalElements(searchValue ? data.length : pagination.totalElements);
        dispatch(setBackofficeChallenges(data));
      } catch (err) {
        console.error(err);
        setError('Erro ao carregar desafios.');
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, [page, selectedCategory, filterStatus, filterType, searchValue, sortBy, sortDir, refreshNonce]);

  const refreshChallenges = () => {
    setPage(1);
    setRefreshNonce(n => n + 1);
  };

  const pointsTotal = challenges.reduce((acc: number, challenge: IChallenges) => acc + (Number(challenge.points) || 0), 0);

  const handleToggleStatus = async (challengeId: string, currentStatus: boolean) => {
    try {
      await toggleChallengeStatus(challengeId, !currentStatus);

      setChallenges(prevChallenges =>
        prevChallenges.map(challenge => {
          if (String(challenge.id) === String(challengeId)) {
            return { ...challenge, active: !currentStatus };
          }
          return challenge;
        })
      );

      setChallengeTotals(prevTotals => {
        const updatedTotals = { ...prevTotals };
        const challenge = challenges.find(c => String(c.id) === String(challengeId));

        if (challenge) {
          const change = currentStatus ? -1 : 1;
          updatedTotals[challenge.pillar] = Math.max(0, updatedTotals[challenge.pillar] + change);
        }
        return updatedTotals;
      });

      return true;
    } catch (error) {
      console.error('Erro ao alterar status do desafio:', error);
      return false;
    }
  };

  const dataCards = [
    { title: 'SECURITY', pillar: EPillar.SECURITY, value: challengeTotals[EPillar.SECURITY] },
    { title: 'ESG', pillar: EPillar.ESG, value: challengeTotals[EPillar.ESG] },
    { title: 'PREDICTIVE', pillar: EPillar.PREDICTIVE, value: challengeTotals[EPillar.PREDICTIVE] },
    { title: 'SAFE', pillar: EPillar.SAFE, value: challengeTotals[EPillar.SAFE] },
  ];

  return {
    challenges,
    pointsTotal,
    totalPages,
    totalElements,
    allChallenges: challenges,
    sortBy,
    sortDir,
    searchValue,
    page,
    setChallenges,
    setSortBy,
    setSortDir,
    setSearchValue,
    setPage,
    challengeTotals,
    dataCards,
    loading,
    error,
    variables,
    pageSize: PAGE_SIZE,
    handleToggleStatus,
    refreshChallenges,
  };
}